import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IconGift, IconInfinity, IconBolt } from "@tabler/icons-react";
import PublicLayout from "../../components/PublicLayout";
import { useLang } from "../../context/LanguageContext";
import { useDashT } from "../../lib/dashboardStrings";
import { CREDIT_PRICING } from "../../lib/platformConstants";

const Join = () => {
  const { lang, setLang } = useLang();
  const dt = useDashT();
  const { pathname } = useLocation();
  // A visitor landing on /sv/... should always see Swedish here, so mirror
  // that into the app language state if not already there. Keeps EVERY string
  // routed through the same dt() dictionary — no local ternaries needed.
  React.useEffect(() => {
    if (pathname.startsWith("/sv") && lang !== "sv") setLang("sv");
  }, [pathname, lang, setLang]);
  const navigate = useNavigate();
  const [email, setEmail] = useState("");

  const bestPer = CREDIT_PRICING.talent[CREDIT_PRICING.talent.length - 1].per_credit_sek;

  const submit = (e) => {
    e.preventDefault();
    if (email) localStorage.setItem("ts_prefill_email", email);
    navigate("/signin");
  };

  const benefits = [
    { Icon: IconGift, color: "#1A8A4A", text: dt("3 free credits to get started") },
    { Icon: IconBolt, color: "#FF5C00", text: dt("Real booking requests with date and budget") },
    { Icon: IconInfinity, color: "#F5F3EE", text: dt("Credits never expire, keep 100% of your fee") },
  ];

  // Value-line: split around <b>{bestPer}</b> so the amount stays bold in JSX
  // without ever emitting a raw HTML string.
  const pricingParts = (() => {
    const template = dt("join_pricing_line")
      .replace("{per}", `__B__${bestPer} ${dt("SEK")}__/B__`)
      .replace("{currency}", "");
    return template.split(/(__B__.*?__\/B__)/g).map((chunk, i) => {
      const m = chunk.match(/^__B__(.*)__\/B__$/);
      return m ? <b key={i} className="text-[#F5F3EE]">{m[1]}</b> : <React.Fragment key={i}>{chunk}</React.Fragment>;
    });
  })();

  return (
    <PublicLayout>
      <section className="max-w-[1000px] mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Hero copy */}
          <div>
            <p className="text-[13px] font-semibold tracking-[0.18em] uppercase text-[#FF5C00]">{dt("Become a talent")}</p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-[#F5F3EE] leading-[1.05]">
              {dt("Get booked by organisers across Sweden")}
            </h1>
            <p className="mt-5 text-[17px] leading-relaxed text-neutral-400">
              {dt("Create your free profile in under two minutes and start receiving matched requests.")}
            </p>
            <div className="mt-8 space-y-3.5" data-testid="join-benefits">
              {benefits.map((b) => (
                <div key={b.text} className="flex items-center gap-3 text-[15px] text-[#F5F3EE]/90">
                  <b.Icon size={22} style={{ color: b.color }} stroke={1.7} />
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[15px] text-neutral-300" data-testid="join-pricing-line">
              {pricingParts}
            </p>
          </div>

          {/* Sign-up form */}
          <div className="rounded-2xl border border-[#252525] bg-[#1A1A1A] p-7" data-testid="join-form-card">
            <h2 className="text-[20px] font-semibold text-[#F5F3EE]">{dt("Create your free profile")}</h2>
            <p className="mt-2 text-[14px] text-neutral-400">{dt("No card needed. No monthly fees.")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="block text-[13px] text-neutral-400 mb-1.5">{dt("Email address")}</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="join-email-input"
                  placeholder="you@email.com"
                  className="w-full h-12 px-3.5 rounded-lg bg-[#111] border border-[#252525] text-[#F5F3EE] focus:outline-none focus:border-[#FF5C00]" />
              </div>
              <button type="submit" data-testid="join-submit-btn"
                className="w-full h-12 rounded-lg bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold flex items-center justify-center gap-2 transition-colors">
                {dt("Create your free profile")} <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Join;
